"use client";

import { useCallback, useState } from "react";
import { validateLeadForm } from "@/helpers/leadFormValidation";

type FieldErrors = Record<string, string>;

/**
 * Inline validation for the lead forms. Forms use `noValidate`, call
 * `validate` on submit, pass `errors[name]` to each field, and put
 * `clearError` on the form's `onInput` so a message goes away as soon as the
 * visitor edits that field.
 */
export function useFieldErrors() {
  const [errors, setErrors] = useState<FieldErrors>({});

  const validate = useCallback(
    (
      form: HTMLFormElement,
      data: Record<string, unknown>,
      requiredFields: string[],
    ) => {
      const nextErrors = validateLeadForm(data, requiredFields) as FieldErrors;
      setErrors(nextErrors);

      // Move to the first field that needs fixing, in page order.
      const firstInvalid = Array.from(form.elements).find(
        (el): el is HTMLElement =>
          el instanceof HTMLElement &&
          "name" in el &&
          Boolean(nextErrors[(el as HTMLInputElement).name]) &&
          (el as HTMLInputElement).type !== "hidden",
      );
      firstInvalid?.focus();

      return Object.keys(nextErrors).length === 0;
    },
    [],
  );

  const clearError = useCallback((event: React.FormEvent<HTMLFormElement>) => {
    const name = (event.target as HTMLInputElement).name;
    if (!name) return;
    setErrors((current) => {
      if (!current[name]) return current;
      const { [name]: _removed, ...rest } = current;
      return rest;
    });
  }, []);

  const resetErrors = useCallback(() => setErrors({}), []);

  return { errors, validate, clearError, resetErrors };
}
